import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useInventory, useInventoryItems } from '../hooks/useInventory';
import { useSession } from '../hooks/useSession';
import { ArrowLeft, Trash2, Download, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import CurrentStats from '../components/inventory/CurrentStats';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

interface UpcFormValues {
  upc: string;
}
interface CountFormValues {
  count: string;
}

const UpdateCountPage: React.FC = () => {
  const { tagCode = '', upc = '' } = useParams();
  const navigate = useNavigate();
  const { batchId } = useSession();

  const {
    updateItemMatchingLastTag,
    updateCount,
    deleteItem,
    exportCsv: exportCsvMutation,
    isLoading,
  } = useInventory();

  const {
    data: inventoryResponse,
    isLoading: itemsLoading,
    refetch,
    error,
  } = useInventoryItems(batchId || '', 1, 100, '');

  const currentItems = inventoryResponse?.data?.items || [];
  const inventoryData = inventoryResponse?.data;

  // Separate loading states for different operations
  const [upcSubmitting, setUpcSubmitting] = useState(false);
  const [countSubmitting, setCountSubmitting] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    item: { batchId: string; tagCode: string; upc: string } | null;
  }>({ isOpen: false, item: null });

  const {
    register: registerUpc,
    handleSubmit: handleUpcSubmit,
    reset: resetUpc,
    setFocus: setUpcFocus,
  } = useForm<UpcFormValues>({ defaultValues: { upc: '' } });

  const {
    register: registerCount,
    handleSubmit: handleCountSubmit,
    reset: resetCount,
  } = useForm<CountFormValues>({ defaultValues: { count: '' } });

  useEffect(() => {
    // Simple focus without aggressive refocusing
    const timer = setTimeout(() => {
      try {
        setUpcFocus('upc');
      } catch (error) {
        console.warn('Initial focus failed:', error);
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [setUpcFocus]);

  const getErrorMessage = (error: any): string => {
    return (
      error?.response?.data?.error ||
      error?.message ||
      (typeof error === 'string' ? error : 'An unexpected error occurred')
    );
  };

  const handleUpcScan = async ({ upc }: UpcFormValues) => {
    const scannedUpc = upc.trim();
    if (!scannedUpc || !batchId) return;

    setUpcSubmitting(true);
    try {
      await updateItemMatchingLastTag({ batchId, tagCode, upc: scannedUpc, count: 1 });
      toast.success('UPC scanned successfully!');
      resetUpc();
      refetch();
      
      // Robust focus solution for server environments
      const focusUpcInput = () => {
        const maxAttempts = 5;
        let attempts = 0;
        
        const tryFocus = () => {
          attempts++;
          
          // Check if element exists and is ready
          const upcInput = document.querySelector('input[name="upc"]') as HTMLInputElement;
          
          if (upcInput && document.body.contains(upcInput) && !upcInput.disabled) {
            try {
              upcInput.focus();
              upcInput.select();
              return true; // Success
            } catch (error) {
              console.warn(`Focus attempt ${attempts} failed:`, error);
            }
          }
          
          // Retry if not successful and under max attempts
          if (attempts < maxAttempts) {
            setTimeout(tryFocus, 100 * attempts); // Increasing delay
          }
          
          return false;
        };
        
        tryFocus();
      };
      
      // Start focusing after a short delay
      setTimeout(focusUpcInput, 50);
      
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setUpcSubmitting(false);
    }
  };

  const handleCountUpdate = async ({ count }: CountFormValues) => {
    const newCount = parseInt(count);
    if (!batchId || isNaN(newCount) || newCount < 0) {
      toast.error('Please enter a valid count');
      return;
    }

    setCountSubmitting(true);
    try {
      await updateCount({ batchId, tagCode, upc, count: newCount });
      toast.success(`Count updated to ${newCount}`);
      resetCount();
      refetch();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCountSubmitting(false);
    }
  };

  const handleDelete = async (batchId: string, tagCode: string, upc: string) => {
    try {
      await deleteItem({ batchId, tagCode, upc });
      toast.success('Item deleted successfully');
      setDeleteDialog({ isOpen: false, item: null });
      refetch();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleExport = async () => {
    try {
      if (!batchId) {
        toast.error('Batch ID is missing');
        return;
      }
      await exportCsvMutation({ batchId });
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Safe navigation that won't be blocked by loading states
  const handleNavigation = (path: string | number) => {
    // Use setTimeout to ensure navigation happens after any pending state updates
    setTimeout(() => {
      if (typeof path === 'number') {
        navigate(path); // For history navigation like -1, 1
      } else {
        navigate(path); // For string paths like '/scan-tag'
      }
    }, 0);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-2">Failed to load inventory data</p>
          <p className="text-sm text-gray-600 mb-4">{getErrorMessage(error)}</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => handleNavigation(-1)}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Update UPC Count</h1>
          </div>
          <button
            onClick={handleExport}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* UPC History */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow border p-6 h-full flex flex-col">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Scanned Items</h3>
              {itemsLoading ? (
                <LoadingSpinner size="sm" text="Loading history..." />
              ) : currentItems.length === 0 ? (
                <p className="text-sm text-gray-500">No items scanned yet.</p>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
                  {currentItems.map((item) => (
                    <div
                      key={`${item.tagCode}-${item.upc}`}
                      className="flex justify-between items-start p-4 bg-gray-50 rounded-lg border border-gray-100 hover:shadow-sm transition"
                    >
                      <div className="space-y-1 text-sm text-gray-700 w-full pr-2">
                        <div className="truncate">
                          <span className="font-semibold text-gray-900">Batch:</span>{' '}
                          {item.batchId}
                        </div>
                        <div className="truncate">
                          <span className="font-semibold text-gray-900">Tag:</span> {item.tagCode}
                        </div>
                        <div className="truncate">
                          <span className="font-semibold text-gray-900">UPC:</span> {item.upc}
                        </div>
                        <div className="truncate">
                          <span className="font-semibold text-gray-900">Qty:</span> {item.count}
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setDeleteDialog({
                            isOpen: true,
                            item: { batchId: item.batchId, tagCode: item.tagCode, upc: item.upc },
                          })
                        }
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-md transition shrink-0"
                        title="Delete item"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="lg:col-span-1">
            <CurrentStats
              tagCode={tagCode}
              upc={upc}
              batchId={batchId}
              inventoryData={inventoryData}
              isLoading={itemsLoading}
            />
          </div>

          {/* Forms */}
          <div className="lg:col-span-1 space-y-6">

            {/* Count Update Form */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Update Total Count</h3>
              <form onSubmit={handleCountSubmit(handleCountUpdate)} className="space-y-4">
                <input
                  {...registerCount('count', {
                    required: 'Count is required',
                    min: { value: 0, message: 'Must be >= 0' },
                    validate: (value) => !isNaN(parseInt(value)) || 'Invalid number',
                  })}
                  type="number"
                  placeholder="Enter total count"
                  disabled={countSubmitting}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="submit"
                  disabled={countSubmitting}
                  className="w-full py-2 px-4 rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {countSubmitting ? 'Updating...' : 'Enter'}
                </button>
              </form>
            </div>

            {/* Navigation Buttons - Never disabled by form submissions */}
            <button
              onClick={() => handleNavigation('/scan-tag')}
              className="w-full py-2 px-4 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Plus className="h-4 w-4 inline mr-2" />
              Next Item
            </button>

            <button
              onClick={() => handleNavigation('/enter-name')}
              className="w-full py-2 px-4 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Plus className="h-4 w-4 inline mr-2" />
              End Current Job
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, item: null })}
        onConfirm={() =>
          deleteDialog.item &&
          handleDelete(deleteDialog.item.batchId, deleteDialog.item.tagCode, deleteDialog.item.upc)
        }
        title="Delete Item"
        message="Are you sure you want to delete this item?"
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
};

export default UpdateCountPage;